/**
 * Seite "Nachweise" eines Antrags im Modul Genehmigung.
 *
 * Zeigt die Nachweise des Antrags in voller Breite, aufgeteilt nach Stand: laufend, offen,
 * abgeschlossen, archiviert (`GRUPPEN`). Jeder Nachweis steht in genau einer Kachel.
 *
 * Angelegt wird hier am Antrag, ohne Baustein: Gutachten und Abfragen werden oft lange vor der
 * ELiA-Erfassung beauftragt (ADR 006). Zugeordnet wird auf der Seite "Antrag" im Baum.
 * „Nachweis anlegen“ ist die Hauptaktion der Seite und steht deshalb oben rechts über allem.
 *
 * Erreichbar unter `/project/{projectId}/permits/{procedureId}/evidences`. Ein geöffneter
 * Nachweis steht als `?nachweis={evidenceId}` in der Adresse – der Link führt direkt in sein
 * Detail, und „Zurück" im Browser schließt es wieder.
 */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Box, Tooltip, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import Button from "../../common/inputs/Button";
import { borderRadius, colors, fonts } from "../../../styles/theme";
import { useSearchParams } from "react-router";
import {
  Evidence,
  EvidenceCreate,
  ProjectDetailPageProps,
} from "../../../types";
import { useAuth } from "../../../auth/AuthProvider";
import { useAntragNavigation, useEditMode } from "../../layout/ProjectLayout";
import PageTemplate from "../../layout/PageTemplate";
import EvidencesSection from "./sections/EvidencesSection";
import EvidenceDetail from "./sections/EvidenceDetail";
import SeitenKopf from "../../layout/SeitenKopf";
import { useEvidenceAktionen } from "./sections/useEvidenceAktionen";
import NachweisAnlegenDialog from "./sections/NachweisAnlegenDialog";
import { bausteinOptionen } from "./sections/bausteinOptionen";
import Zeitstrahl from "../../common/display/Zeitstrahl";
import { zeitstrahlPunkte } from "./sections/nachweisZeitstrahl";
import { useProjectEvidences } from "./hooks/useProjectEvidences";
import { usePermitLookups } from "./hooks/usePermitLookups";
import { useProjectDeadlines } from "../../../hooks/useProjectDeadlines";
import DeadlinesSection from "../deadlines/DeadlinesSection";
import ManualDeadlineDialog from "../deadlines/ManualDeadlineDialog";
import { manualDeadlinesOf } from "../../../utils/deadlines";
import { useProjektRechte } from "../../../contexts/ProjektRechteContext";
import { CostAccess } from "./sections/EvidenceUebergangDialog";

/**
 * Die Kacheln der Seite, in dieser Reihenfolge. Jeder Nachweis steht in genau einer; archiviert
 * geht vor dem Status. Kacheln ohne Nachweis entfallen.
 */
const GRUPPEN: {
  key: string;
  titel: string;
  gehoertDazu: (e: Evidence) => boolean;
}[] = [
  {
    key: "laufend",
    titel: "Laufende Nachweise",
    gehoertDazu: (e) => !e.archiviert && e.status !== "Offen" && !e.completed,
  },
  {
    key: "offen",
    titel: "Offene Nachweise",
    gehoertDazu: (e) => !e.archiviert && e.status === "Offen",
  },
  {
    key: "abgeschlossen",
    titel: "Abgeschlossene Nachweise",
    gehoertDazu: (e) => !e.archiviert && e.completed,
  },
  {
    key: "archiviert",
    titel: "Archivierte Nachweise",
    gehoertDazu: (e) => e.archiviert,
  },
];

const GRUPPE_NACH_KEY = Object.fromEntries(
  GRUPPEN.map((gruppe) => [gruppe.key, gruppe.gehoertDazu]),
) as Record<string, (e: Evidence) => boolean>;

/**
 * Die drei Anteile des Balkens, in der Reihenfolge des Ablaufs. Farben aus dem Farbsystem, eine
 * Stufe heller als die Badges (Stufe 200 statt 500/600) – dieselben Pastelltöne wie die Segmente
 * des Reifegrads.
 */
const ANTEILE = [
  { key: "offen", label: "Offen", farbe: colors.reifegradSegment.warning },
  { key: "laufend", label: "Laufend", farbe: colors.brand.teal200 },
  {
    key: "abgeschlossen",
    label: "Abgeschlossen",
    farbe: colors.reifegradSegment.success,
  },
] as const;

type Anteil = (typeof ANTEILE)[number]["key"];

/**
 * Stand der Freigaben neben dem Zeitstrahl: große Zahl, daneben die Gesamtzahl, darunter ein
 * Balken mit den Anteilen abgeschlossen, laufend und offen. Ohne Kachel, damit sie mit dem
 * Zeitstrahl auf dem Seitenhintergrund steht. Archivierte zählen nicht mit (ADR 007).
 */
const FreigabeKennzahl: React.FC<{ anzahl: Record<Anteil, number> }> = ({
  anzahl,
}) => {
  const gesamt = anzahl.abgeschlossen + anzahl.laufend + anzahl.offen;
  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.75 }}>
        <Typography
          sx={{
            fontSize: "1.75rem",
            lineHeight: 1,
            fontWeight: fonts.weights.semiBold,
            color: colors.text.primary,
          }}
        >
          {anzahl.abgeschlossen}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: colors.text.secondary,
            fontWeight: fonts.weights.medium,
          }}
        >
          / {gesamt} abgeschlossen
        </Typography>
      </Box>
      <Box
        role="img"
        aria-label={ANTEILE.map(
          (a) => `${anzahl[a.key]} ${a.label.toLowerCase()}`,
        ).join(", ")}
        sx={{
          mt: 1,
          display: "flex",
          height: 6,
          // Jeder Anteil ist für sich eine Pille; die Fuge trennt sie.
          gap: "3px",
        }}
      >
        {gesamt > 0 &&
          ANTEILE.filter((a) => anzahl[a.key] > 0).map((a) => (
            <Tooltip key={a.key} arrow title={`${a.label}: ${anzahl[a.key]}`}>
              <Box
                sx={{
                  width: `${(anzahl[a.key] / gesamt) * 100}%`,
                  borderRadius: `${borderRadius.full}px`,
                  backgroundColor: a.farbe,
                }}
              />
            </Tooltip>
          ))}
      </Box>
    </Box>
  );
};

/** Query-Parameter für den geöffneten Nachweis */
const NACHWEIS_PARAM = "nachweis";
/** Query-Parameter: das Detail zu den Terminen des Nachweises scrollen – einmalig, danach entfernt */
const DEADLINES_PARAM = "termine";

interface PermitEvidencesPageProps extends ProjectDetailPageProps {
  procedureId: string;
  canView: boolean;
  canEdit: boolean;
}

const PermitEvidencesPage: React.FC<PermitEvidencesPageProps> = ({
  project,
  procedureId,
  canEdit,
}) => {
  const { isEditMode } = useEditMode();
  const { oeffneAntrag } = useAntragNavigation();
  const { user } = useAuth();
  const {
    darfSehen,
    darfBearbeiten,
    geladen: rechteGeladen,
  } = useProjektRechte();
  const [anlegenOffen, setAnlegenOffen] = useState(false);
  /** Nachweis, an dem „Termin anlegen" gerade offen ist */
  const [deadlineForEvidence, setDeadlineForEvidence] = useState<string | null>(
    null,
  );

  const [searchParams, setSearchParams] = useSearchParams();
  /** Im Zeitstrahl gewählter Nachweis – markiert seine Zeile, öffnet aber nicht das Detail */
  const [markierterNachweisId, setMarkierterNachweisId] = useState<
    string | null
  >(null);
  const nachweisImLink = searchParams.get(NACHWEIS_PARAM);
  const scrollToDeadlines = searchParams.has(DEADLINES_PARAM);
  /** Kachel „Termine" im Detail – Ziel des Hinweises „+1 Termin" in der Liste */
  const deadlinesRef = useRef<HTMLDivElement>(null);

  const openDeadlines = useCallback(
    (evidenceId: string) =>
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set(NACHWEIS_PARAM, evidenceId);
        next.set(DEADLINES_PARAM, "1");
        return next;
      }),
    [setSearchParams],
  );

  const setSelectedEvidenceId = useCallback(
    (evidenceId: string | null, optionen?: { replace?: boolean }) =>
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (evidenceId) next.set(NACHWEIS_PARAM, evidenceId);
          else next.delete(NACHWEIS_PARAM);
          return next;
        },
        { replace: optionen?.replace },
      ),
    [setSearchParams],
  );

  const {
    evidences,
    costItems,
    antrag,
    loading,
    error,
    createEvidence,
    updateEvidence,
    transitionEvidence: transitionEvidenceRaw,
    loadEvidenceHistorie,
    deleteEvidence: deleteEvidenceRaw,
    saveCost,
    isSaving,
    saveError,
  } = useProjectEvidences({
    projectId: project?.id,
    procedureId,
    mayReadCosts: rechteGeladen ? darfSehen("genehmigung.kosten") : undefined,
  });

  // Termine des Antrags: „Nächster Termin", Zeitstrahl, Rückfragen und die Kachel im Detail.
  const deadlines = useProjectDeadlines(project?.id, {
    permitProcedureId: procedureId,
  });
  const { reload: reloadDeadlines } = deadlines;
  // Archivieren und Löschen räumen manuelle Termine mit ab – danach neu laden.
  const transitionEvidence = useCallback(
    async (...args: Parameters<typeof transitionEvidenceRaw>) => {
      const result = await transitionEvidenceRaw(...args);
      await reloadDeadlines();
      return result;
    },
    [transitionEvidenceRaw, reloadDeadlines],
  );
  const deleteEvidence = useCallback(
    async (evidenceId: string) => {
      await deleteEvidenceRaw(evidenceId);
      await reloadDeadlines();
    },
    [deleteEvidenceRaw, reloadDeadlines],
  );

  const {
    evidenceTypeOptions,
    processingPathOptions,
    nachweisKatalog,
    componentTypeOptions,
    orgUsers,
    loading: lookupsLoading,
  } = usePermitLookups();

  // Ein gelöschter oder fremder Nachweis im Link öffnet die Liste. Erst nach dem Laden – vorher
  // ist die Liste leer, und der Link ginge verloren.
  const selectedEvidenceId =
    nachweisImLink &&
    evidences.some((evidence) => evidence.id === nachweisImLink)
      ? nachweisImLink
      : null;
  const nachweisFehlt = !loading && !!nachweisImLink && !selectedEvidenceId;
  useEffect(() => {
    if (nachweisFehlt) setSelectedEvidenceId(null, { replace: true });
  }, [nachweisFehlt, setSelectedEvidenceId]);

  const editable = canEdit && isEditMode;
  // Die Kosten haben ein eigenes Recht („Genehmigung › Kosten“): ohne Leserecht keine Beträge im
  // Detail und im Dialog, ohne Schreibrecht nur lesend.
  const costAccess: CostAccess =
    editable && darfBearbeiten("genehmigung.kosten")
      ? "write"
      : darfSehen("genehmigung.kosten")
        ? "read"
        : "none";
  const costs = useMemo(
    () => ({
      itemOf: (evidenceId: string) =>
        costItems.find((item) => item.evidence_id === evidenceId),
      access: costAccess,
    }),
    [costItems, costAccess],
  );
  const isLoading = loading || lookupsLoading;

  // Ein gelöschter oder fremder Antrag in der URL führt zur Liste statt ins Leere.
  const antragFehlt = !isLoading && !error && !antrag;
  useEffect(() => {
    if (antragFehlt) oeffneAntrag(null, undefined, { replace: true });
  }, [antragFehlt, oeffneAntrag]);

  const gruppen = useMemo(
    () =>
      GRUPPEN.map((gruppe) => ({
        ...gruppe,
        evidences: evidences.filter(gruppe.gehoertDazu),
      })).filter((gruppe) => gruppe.evidences.length > 0),
    [evidences],
  );

  const punkte = useMemo(
    () => zeitstrahlPunkte(evidences, deadlines.deadlines),
    [evidences, deadlines.deadlines],
  );

  // Gezählt wie im Antragsfortschritt: ohne archivierte (ADR 007). Dieselbe Aufteilung wie die
  // Kacheln darunter, damit Balken und Kacheln nie verschieden zählen.
  const anzahl: Record<Anteil, number> = {
    abgeschlossen: evidences.filter(GRUPPE_NACH_KEY.abgeschlossen).length,
    laufend: evidences.filter(GRUPPE_NACH_KEY.laufend).length,
    offen: evidences.filter(GRUPPE_NACH_KEY.offen).length,
  };

  /** Was alle Kacheln gleich bekommen – nur Titel und Ausschnitt unterscheiden sich */
  const gemeinsam = {
    orgUsers,
    canEdit: editable,
    onSelectEvidence: (evidenceId: string) => setSelectedEvidenceId(evidenceId),
    onUpdate: updateEvidence,
    onTransition: transitionEvidence,
    onDelete: deleteEvidence,
    deadlines: deadlines.deadlines,
    onOpenDeadlines: openDeadlines,
    onAddDeadline: editable
      ? (evidence: Evidence) => setDeadlineForEvidence(evidence.id)
      : undefined,
    costs,
  };

  // Menü und Dialog der Aktionen im geöffneten Nachweis; die Kacheln haben ihre eigenen.
  const {
    oeffneMenu,
    oeffneAktion,
    changePath,
    elemente: aktionsElemente,
  } = useEvidenceAktionen(
    transitionEvidence,
    (evidenceId) =>
      manualDeadlinesOf(deadlines.deadlines, { evidenceId }).length,
    costs,
  );
  const selectedEvidence =
    evidences.find((evidence) => evidence.id === selectedEvidenceId) ?? null;

  // Kommt man über den Hinweis auf manuelle Termine ins Detail, steht es bei seinen Terminen.
  // Der Parameter gilt nur für diesen Sprung – sonst scrollte jedes spätere Rendern erneut.
  useEffect(() => {
    if (!scrollToDeadlines || !selectedEvidence) return;
    const frame = requestAnimationFrame(() => {
      deadlinesRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete(DEADLINES_PARAM);
          return next;
        },
        { replace: true },
      );
    });
    return () => cancelAnimationFrame(frame);
  }, [scrollToDeadlines, selectedEvidence, setSearchParams]);

  const handleAnlegen = async (data: EvidenceCreate): Promise<boolean> => {
    const erstellt = await createEvidence(procedureId, data);
    return !!erstellt;
  };

  return (
    <PageTemplate
      isLoading={isLoading}
      error={error}
      isSaving={isSaving}
      saveError={saveError}
      // Die Übersicht trägt den Titel aus der Navigation („Nachweise").
      // Ein geöffneter Nachweis ist eine Ebene tiefer: Über seiner Bezeichnung steht der
      // Rückweg zu den Nachweisen, der Antrag steht schon im Seitenkopf.
      seitenTitel={
        selectedEvidence
          ? {
              back: {
                label: "Nachweise",
                onClick: () => setSelectedEvidenceId(null),
              },
              titel: selectedEvidence.bezeichnung || "Ohne Bezeichnung",
            }
          : undefined
      }
    >
      {/* Mit geöffnetem Nachweis steht nur sein Detail da, ohne die Kacheln der Übersicht. */}
      {selectedEvidence ? (
        <EvidenceDetail
          evidence={selectedEvidence}
          orgUsers={orgUsers}
          evidenceTypeOptions={evidenceTypeOptions}
          bausteinOptionen={bausteinOptionen(
            componentTypeOptions,
            nachweisKatalog.find(
              (eintrag) =>
                eintrag.id === selectedEvidence.evidence_catalog_entry_id,
            ),
          )}
          canEdit={editable}
          onSave={updateEvidence}
          costItem={costs.itemOf(selectedEvidence.id)}
          costAccess={costAccess}
          onCostSave={(data) => saveCost(selectedEvidence.id, data)}
          onOeffneAktion={(aktion) => oeffneAktion(selectedEvidence, aktion)}
          onChangePath={(path) => void changePath(selectedEvidence, path)}
          onOeffneMenu={(anchor) =>
            oeffneMenu(selectedEvidence, anchor, {
              ohneHauptaktion: true,
              onAddDeadline: editable
                ? () => setDeadlineForEvidence(selectedEvidence.id)
                : undefined,
            })
          }
          ladeHistorie={loadEvidenceHistorie}
          // Auch geänderte Kosten stehen in der Historie, ohne den Nachweis selbst zu ändern.
          historyKey={[
            ...manualDeadlinesOf(deadlines.deadlines, {
              evidenceId: selectedEvidence.id,
            }).map((d) => `${d.id}:${d.done_at ?? ""}`),
            costs.itemOf(selectedEvidence.id)?.updated_at ?? "",
          ].join("|")}
          renderDeadlinesSection={({ fieldRows, onFieldRowClick }) =>
            project && (
              <Box ref={deadlinesRef}>
                <DeadlinesSection
                  projectId={project.id}
                  // Nur die manuellen: Die abgeleitete Frist des Nachweises steht schon als
                  // Fristen-Feld in `fieldRows`.
                  controller={{
                    ...deadlines,
                    deadlines: deadlines.deadlines.filter(
                      (d) =>
                        d.evidence_id === selectedEvidence.id &&
                        d.source === "manual",
                    ),
                  }}
                  fieldRows={fieldRows}
                  onFieldRowClick={onFieldRowClick}
                  collapseFurther
                  canEdit={editable && !selectedEvidence.archiviert}
                  permitProcedureId={procedureId}
                  evidenceId={selectedEvidence.id}
                  showReference={false}
                  noOuterPadding
                />
              </Box>
            )
          }
        />
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {/* Ohne Kachel auf dem Seitenhintergrund: der Überblick über allem. Links immer der
              Zeitstrahl, auch ohne Termine – er zeigt dann den Weg bis zum Zieltermin. Rechts ein
              Block hinter einer Linie: oben die Hauptaktion der Seite, gefüllt, damit sie sich
              von den umrandeten Knöpfen in den Zeilen abhebt; darunter der Stand der Freigaben.
              Knopf und Kennzahl sind gleich breit, damit beide links und rechts fluchten. */}
          <SeitenKopf
            aktionen={
              <>
                {editable && (
                  <Button
                    variant="primary"
                    startIcon={<AddIcon />}
                    // Nie zweizeilig – der Block wächst mit der Beschriftung. Ist er breiter
                    // (Mindestbreite), füllt der Knopf ihn trotzdem ganz aus.
                    fullWidth
                    sx={{ whiteSpace: "nowrap" }}
                    onClick={() => setAnlegenOffen(true)}
                  >
                    Nachweis anlegen
                  </Button>
                )}
                <FreigabeKennzahl anzahl={anzahl} />
              </>
            }
          >
            <Zeitstrahl
              punkte={punkte}
              ende={antrag?.genehmigung_geplant_am}
              endeLabel="Zieltermin"
              selectedId={markierterNachweisId}
              onSelect={setMarkierterNachweisId}
            />
          </SeitenKopf>
          {evidences.length === 0 && (
            <EvidencesSection evidences={evidences} {...gemeinsam} />
          )}
          {gruppen.map((gruppe) => (
            <React.Fragment key={gruppe.key}>
              <EvidencesSection
                title={gruppe.titel}
                evidences={gruppe.evidences}
                alleEvidences={evidences}
                vorsortiert
                markierteEvidenceId={markierterNachweisId}
                // Der Stand der Freigaben steht oben als Kennzahl, nicht noch einmal im Kopf.
                mitZaehler={false}
                {...gemeinsam}
              />
            </React.Fragment>
          ))}
        </Box>
      )}
      {aktionsElemente}
      {project && (
        <ManualDeadlineDialog
          open={!!deadlineForEvidence}
          projectId={project.id}
          presetPermitProcedureId={procedureId}
          presetEvidenceId={deadlineForEvidence}
          onClose={() => setDeadlineForEvidence(null)}
          onSubmit={deadlines.submit}
        />
      )}
      <NachweisAnlegenDialog
        open={anlegenOffen}
        nachweisKatalog={nachweisKatalog}
        evidenceTypeOptions={evidenceTypeOptions}
        processingPathOptions={processingPathOptions}
        orgUsers={orgUsers}
        componentTypeOptions={componentTypeOptions}
        aktuellerBenutzerId={user?.id}
        onClose={() => setAnlegenOffen(false)}
        onSubmit={handleAnlegen}
      />
    </PageTemplate>
  );
};

export default PermitEvidencesPage;
